/**
 * Display rules for admin-deactivated (archived) accounts on historical
 * content: group message bubbles, announcement/forum bylines, call history,
 * transcript speaker labels. One canonical label, no avatar, no profile tap,
 * no call-back. Self-service deletion ("Deleted User") is a separate flow.
 */
import {
  DEACTIVATED_USER_LABEL,
  isInactiveUserStatus,
} from '@shared/users/deactivated-user'
import type { ChatUser } from '../types/chat'

export { DEACTIVATED_USER_LABEL }

type UserLike = { status?: string | null; displayName?: string | null } | null | undefined

/** True when this directory/stub user is admin-deactivated. */
export function isDeactivatedUser(user: UserLike): boolean {
  return isInactiveUserStatus(user?.status)
}

/**
 * Public display name for a user on historical content: the canonical
 * "Deactivated user" label when archived, else their live display name,
 * else the caller's fallback (e.g. 'User' / 'Unknown').
 */
export function displayNameForUser(user: UserLike, fallback = 'User'): string {
  if (isDeactivatedUser(user)) return DEACTIVATED_USER_LABEL
  return user?.displayName?.trim() || fallback
}

/**
 * Redacted ChatUser stub for a deactivated account — safe to merge into
 * `usersById` maps used by historical surfaces. Carries no email, avatar,
 * role, or real name.
 */
export function makeDeactivatedUserStub(userId: string): ChatUser {
  return {
    userId,
    email: '',
    displayName: DEACTIVATED_USER_LABEL,
    givenName: null,
    familyName: null,
    roleType: null,
    providerLevel: null,
    avatarUrl: null,
    title: null,
    settingsJson: null,
    presenceStatus: null,
    lastActiveAt: null,
    status: 'INACTIVE',
  }
}
